import { useGetAccountByIdQuery } from '@/redux/api/accounts.rtkq';
import { useAuth } from '@/hooks/useAuth';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getAppRemainingCredits } from '@/utils/appAccess';

interface QuotaDisplayProps {
  className?: string;
}

const SCAN_APP_ID = 'scan-documents-extraction';

/**
 * Compact dashboard-style quota display for scan documents extraction.
 * Remaining = window pool + bonus (same as canUseApp). Used is clamped to the
 * plan window so the bar never shows used > total.
 */
export const QuotaDisplay = ({ className }: QuotaDisplayProps) => {
  const auth = useAuth();
  const officeId = auth.getAccountId();
  const { data: account, isLoading } = useGetAccountByIdQuery(
    { accountId: officeId! },
    { skip: !officeId },
  );

  if (isLoading || !account) {
    return (
      <div
        className={`flex items-center gap-3 px-4 py-2 bg-background border border-border rounded-xl ${className}`}
      >
        <Skeleton className="h-8 w-12" />
        <div className="w-px h-8 bg-border" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-2 w-24" />
        </div>
      </div>
    );
  }

  const scanDocumentsApp = account.subscription.plan.includedApps.find(
    (app) => app.appIdentifier === SCAN_APP_ID,
  );

  const { includedCredits, creditCost } = scanDocumentsApp || {};
  const hasCredits = Boolean(scanDocumentsApp);

  const remainingCredits = getAppRemainingCredits(account, SCAN_APP_ID);
  const totalCredits = includedCredits || 1000;
  const windowRemaining = account.appCreditBalances?.[SCAN_APP_ID] || 0;
  const usedCredits = Math.max(
    0,
    Math.min(totalCredits, totalCredits - windowRemaining),
  );
  const usagePercentage =
    totalCredits > 0
      ? Math.min((usedCredits / totalCredits) * 100, 100)
      : 0;

  const formatNumber = (num: number) => num.toLocaleString('he-IL');

  const tooltipText =
    creditCost != null ? `עמוד = ${creditCost} קרדיט` : 'עמוד = 1 קרדיט';

  if (!hasCredits) {
    return null;
  }

  return (
    <div
      className={`flex items-center gap-4 px-5 py-3 bg-background border border-border rounded-xl shadow-sm ${className}`}
    >
      <div className="flex flex-col items-center">
        <span className="text-xs text-muted-foreground">נותרו</span>
        <span className="text-xl font-bold text-chen-900">
          {formatNumber(remainingCredits)}
        </span>
      </div>

      <div className="w-px h-10 bg-border" />

      <div className="flex flex-col gap-1.5 min-w-[180px]">
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-chen-900 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span className="text-sm text-foreground">
            {formatNumber(usedCredits)} נוצלו מתוך {formatNumber(totalCredits)}
          </span>
        </div>
        <Progress value={usagePercentage} className="h-2" />
      </div>
    </div>
  );
};
